import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Navigation, Footer } from "../components/landing/Layout";
import { Button } from "../components/ui/button";
import { Bell, MessageSquare, PhoneCall, QrCode, ShieldCheck, Smartphone } from "lucide-react";
import { useI18n } from "../i18n";

const API = "/api";

/**
 * RC-WEB-3 §8 — durable public destination that replaces the PWA-first
 * "Cómo activar avisos" page. Store badges are rendered ONLY when the master admin
 * has published a real store URL (`shared_content.app_links`), so no code change is
 * needed the day the Android/iOS apps ship.
 */
const STEPS = [
  { icon: QrCode, key: "step_qr" },
  { icon: Smartphone, key: "step_scan" },
  { icon: MessageSquare, key: "step_message" },
  { icon: PhoneCall, key: "step_call" },
  { icon: Bell, key: "step_alerts" },
];

export default function HowToUse() {
  const { t, lang } = useI18n();
  const [appLinks, setAppLinks] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/shared/content?locale=${lang || "es"}`)
      .then((r) => setAppLinks(r.data?.app_links || {}))
      .catch(() => setAppLinks({}));
  }, [lang]);

  const androidUrl = (appLinks?.android_store_url || "").trim();
  const iosUrl = (appLinks?.ios_store_url || "").trim();

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="how-to-use-page">
      <Navigation />

      <section className="bg-[#0B2545] py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#1B84FF]/20 px-4 py-2 text-sm font-semibold text-[#5BC0FF]">
            <ShieldCheck className="h-4 w-4" />
            {t("howto.badge")}
          </div>
          <h1
            className="mt-6 text-4xl font-bold text-white sm:text-5xl lg:text-6xl"
            style={{ fontFamily: "Manrope, sans-serif" }}
            data-testid="how-to-use-title"
          >
            {t("howto.title")}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-slate-300 md:text-lg">{t("howto.subtitle")}</p>
        </div>
      </section>

      <section className="py-14">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <ol className="space-y-4" data-testid="how-to-use-steps">
            {STEPS.map((step, i) => {
              const Icon = step.icon;
              return (
                <li
                  key={step.key}
                  className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-colors hover:border-[#1B84FF]/40"
                  data-testid={`how-to-use-step-${i + 1}`}
                >
                  <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#1B84FF]/10">
                    <Icon className="h-6 w-6 text-[#1B84FF]" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-bold text-[#0B2545]">{t(`howto.${step.key}_title`)}</p>
                    <p className="mt-1 text-sm text-slate-600">{t(`howto.${step.key}_text`)}</p>
                  </div>
                </li>
              );
            })}
          </ol>

          {(androidUrl || iosUrl) && (
            <div
              className="mt-10 rounded-2xl border border-slate-200 bg-white p-6"
              data-testid="how-to-use-store-links"
            >
              <p className="font-bold text-[#0B2545]">{t("howto.store_title")}</p>
              <div className="mt-4 flex flex-wrap gap-3">
                {androidUrl && (
                  <a href={androidUrl} target="_blank" rel="noopener noreferrer" data-testid="store-badge-android">
                    <Button className="rounded-full bg-[#1B84FF] hover:bg-[#0F6FDB]">
                      {t("howto.store_android")}
                    </Button>
                  </a>
                )}
                {iosUrl && (
                  <a href={iosUrl} target="_blank" rel="noopener noreferrer" data-testid="store-badge-ios">
                    <Button className="rounded-full bg-[#0B2545] hover:bg-[#123763]">
                      {t("howto.store_ios")}
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}

          <div
            className="mt-10 rounded-2xl border border-slate-200 bg-white p-6"
            data-testid="how-to-use-browser-fallback"
          >
            <p className="font-bold text-[#0B2545]">{t("howto.browser_title")}</p>
            <p className="mt-2 text-sm text-slate-600">{t("howto.browser_text")}</p>
            <Link to="/ayuda/usar-en-navegador" className="mt-4 inline-block">
              <Button variant="outline" className="rounded-full" data-testid="how-to-use-browser-link">
                {t("howto.browser_cta")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
